import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api, fileUrl } from "../api";
import {
  Rule,
  Footer,
  Loading,
  Nav,
  ScatterBand,
  Verse,
  ErrorNote,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

const ICON_CLASS = {
  hand: "ic-hand",
  lattice: "ic-lattice",
  flame: "ic-flame",
  well: "ic-well",
  chalice: "ic-chalice",
};

/* The handout block — a printed thing to take away, not a software download. */
export function Handbill() {
  return (
    <div className="handbill" data-testid="rule-handbill">
      <p className="eyebrow sc">
        <span className="num">&middot;</span>
        <span>Handout</span>
      </p>
      <h3>Download the Rule as a PDF.</h3>
      <p>US Letter. Made for printing, margin notes, and coffee rings.</p>
      <a
        className="act"
        href={fileUrl("/rule/pdf")}
        data-testid="rule-download-pdf-link"
      >
        Get the PDF &rarr;
      </a>
      <div className="orn" aria-hidden="true" />
    </div>
  );
}

function Paras({ items }) {
  return (items || []).map((p, i) => <p key={i}>{p}</p>);
}

function BeliefBody({ items }) {
  return (items || []).map((item, i) =>
    typeof item === "object" && item.quote ? (
      <blockquote className="q" key={i}>
        {item.quote}
      </blockquote>
    ) : (
      <p key={i}>{item}</p>
    ),
  );
}

function Section({ sec }) {
  return (
    <section data-testid={`rule-section-${sec.numeral}`}>
      <div className="prose">
        <p className="eyebrow sc">
          <span className="num">{sec.numeral}</span>
          <span>{sec.eyebrow}</span>
        </p>
        <h2>{sec.heading}</h2>

        {sec.paras && sec.paras.length === 1 ? (
          <p className="lede">{sec.paras[0]}</p>
        ) : (
          <Paras items={sec.paras} />
        )}

        {/* II — the four senses */}
        {sec.senses ? (
          <div className="grid four" style={{ margin: "2.4rem 0" }}>
            {sec.senses.map((s) => (
              <div className="cell" key={s.idx}>
                <span className="sc">
                  {s.idx} &middot; {s.label}
                </span>
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        ) : null}

        <Paras items={sec.closing} />

        {sec.aside ? (
          <div className="aside-note">
            <p>{sec.aside.body}</p>
            <span className="sig">{sec.aside.sig}</span>
          </div>
        ) : null}

        {/* III — the seven beliefs */}
        {sec.beliefs
          ? sec.beliefs.map((b) => (
              <article className="belief" key={b.num} data-testid={`rule-belief-${b.num}`}>
                <div className="belief-head">
                  <span className="belief-num">{b.num}</span>
                  <h3 className="belief-claim">{b.claim}</h3>
                </div>
                <div className="belief-body">
                  <p>{b.lede}</p>

                  {b.refs ? (
                    <dl className="refs">
                      {b.refs.map((r) => (
                        <React.Fragment key={r.term}>
                          <dt>{r.term}</dt>
                          <dd>{r.def}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  ) : null}

                  {b.body && b.body.length ? (
                    <details className="fold">
                      <summary data-testid={`rule-belief-${b.num}-expander`}>
                        Read on
                      </summary>
                      <div className="fold-body">
                        <BeliefBody items={b.body} />
                      </div>
                    </details>
                  ) : null}
                </div>
              </article>
            ))
          : null}

        {/* IV — the practices */}
        {sec.practices
          ? sec.practices.map((p) => (
              <div className="practice" key={p.key} data-testid={`rule-practice-${p.key}`}>
                <div
                  className={`practice-icon ${ICON_CLASS[p.icon] || ""}`}
                  aria-hidden="true"
                >
                  <i />
                </div>
                <div>
                  <h3>{p.name}</h3>
                  <p>{p.body}</p>
                </div>
              </div>
            ))
          : null}

        {/* V — the habits */}
        {sec.habits ? (
          <div style={{ marginTop: "1.6rem" }}>
            {sec.habits.map((h, i) => (
              <div className="habit" key={i} data-testid={`rule-habit-${i + 1}`}>
                <h3>{h.name}</h3>
                <p>{h.body}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function RulePage() {
  useBodyClass("inner");
  useDocumentTitle(
    "The Rule",
    "Sch\u0113ma's rule: what we mean by Christian art, what we actually do, and the habits we hold each other to.",
  );

  const { data, error, isLoading, mutate } = useSWR("/rule", fetcher);

  if (isLoading) {
    return (
      <>
        <Nav />
        <Loading label="Setting the type" testid="rule-loading" />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <section>
          <ErrorNote
            message="We couldn't load the Rule just now."
            onRetry={() => mutate()}
            testid="rule-error"
          />
        </section>
        <Footer />
      </>
    );
  }

  const { meta, sections } = data;

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="rule-page-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>{meta.eyebrow}</span>
          </p>
          <h1>{meta.title}</h1>
          <p className="standfirst">{meta.standfirst}</p>
        </div>
      </header>

      <section style={{ paddingBottom: 0 }} data-testid="rule-handout-top">
        <div className="prose">
          <Handbill />

          {/* The essay sits beside the Rule: the why next to the shape. */}
          <aside className="side-page" data-testid="rule-essay-cue">
            <p className="eyebrow sc">
              <span className="num">&middot;</span>
              <span>Alongside this</span>
            </p>
            <h3>
              <Link to="/rule/why" data-testid="rule-essay-link">
                Why Sch&#275;ma?
              </Link>
            </h3>
            <p>
              An essay by Austin on why any of this matters: the aesthetic
              reckoning, the return to sincerity, and where the name comes from.
            </p>
            <Link to="/rule/why" className="linkish" data-testid="rule-essay-go">
              Read the essay &rarr;
            </Link>
          </aside>
        </div>
      </section>

      {sections.length ? (
        <div className="rule-read long-read">
          {sections.map((sec, i) => (
            <React.Fragment key={sec.numeral}>
              <Section sec={sec} />
              {i < sections.length - 1 ? (
                i % 2 === 0 ? (
                  <Rule />
                ) : (
                  <ScatterBand />
                )
              ) : null}
            </React.Fragment>
          ))}
        </div>
      ) : null}

      <Rule />

      <section data-testid="rule-handout-bottom">
        <div className="prose">
          <Handbill />
        </div>
      </section>

      <Verse />

      <section className="cta-block" data-testid="rule-cta">
        <div className="prose">
          <h2>Come to something.</h2>
          <p>
            That&rsquo;s the entry process. Come once, see whether the room is any
            good, come again.
          </p>
          <Link to="/join" className="button" data-testid="rule-join-cta">
            Get in touch
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
